import { useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, Mic, Flame, Users } from "lucide-react";

const TOPICS = [
  { icon: Flame, label: "Forged in Fire", body: "Leadership lessons from the fireground — the moments that reveal who you really are." },
  { icon: Users, label: "Trust Under Pressure", body: "How great leaders build teams that hold together when the heat is on." },
  { icon: Mic, label: "Decisions When It Matters", body: "Frameworks for clarity in conditions of incomplete information and high consequence." },
];

export default function Speak() {
  const [form, setForm] = useState({
    name: "", email: "", organization: "", event_type: "keynote",
    event_date: "", audience_size: "", message: "",
  });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/speaking/inquiries", form);
      setDone(true);
      toast.success("Inquiry sent — we'll reply within 48 hours.");
    } catch {
      toast.error("Could not send. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <SEO
        title="Book Robbie South to Speak"
        description="Keynotes, workshops, and leadership intensives for fire services, executive teams, and faith-based organizations — built on twenty years on the fireground."
      />
      <section className="border-b border-brand-line">
        <div className="container-narrow py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="overline text-brand-rust">Speaking & Workshops</span>
            <h1 className="font-display text-5xl md:text-6xl mt-4 text-brand-ink leading-[1.05]">
              Book Robbie to speak.
            </h1>
            <p className="mt-6 text-lg text-brand-inkMuted max-w-2xl leading-relaxed">
              Keynotes, workshops, and leadership intensives for fire services, public-safety agencies, executive teams, and faith-based organizations. Built on twenty years on the fireground and the lessons in <em>Forged in Fire</em>.
            </p>
          </div>
          <div className="lg:col-span-5 relative" data-testid="speak-portrait">
            <div className="absolute -inset-3 bg-brand-rust/15 -z-0 hidden lg:block" />
            <img
              src="https://customer-assets.emergentagent.com/job_lead-store/artifacts/lak5i4uk_RobbieSouth.jpg"
              alt="Robbie South speaking"
              className="relative w-full aspect-[4/5] object-cover grayscale-[0.05]"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Topics */}
      <section className="container-narrow py-20">
        <div className="grid md:grid-cols-3 gap-px bg-brand-line">
          {TOPICS.map((t, i) => {
            const Icon = t.icon;
            return (
              <div key={t.label} className="bg-white p-8" data-testid={`speak-topic-${i}`}>
                <Icon className="w-6 h-6 text-brand-rust" />
                <p className="font-serif text-2xl mt-4 text-brand-ink leading-snug">{t.label}</p>
                <p className="mt-3 text-brand-inkMuted leading-relaxed text-sm">{t.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Inquiry form */}
      <section className="border-t border-brand-line bg-white">
        <div className="container-narrow py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="overline text-brand-rust">Send an inquiry</span>
            <h2 className="font-display text-4xl mt-4 text-brand-ink leading-[1.05]">Tell us about your event.</h2>
            <p className="mt-6 text-brand-inkMuted leading-relaxed">
              The more context you can share — audience, theme, dates, location — the better we can shape the conversation. Robbie or his team will respond within 48 hours.
            </p>
            <ul className="mt-8 space-y-2 text-sm text-brand-inkMuted">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-rust" /> 45-min keynotes, half-day workshops, multi-session intensives</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-rust" /> Travel from the US Southeast, available worldwide</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-rust" /> Book copies & companion workshops available</li>
            </ul>
          </div>
          <div className="lg:col-span-7">
            {done ? (
              <div className="bg-brand-bg border border-brand-line p-12 text-center" data-testid="speak-success">
                <CheckCircle2 className="w-10 h-10 mx-auto text-brand-rust" />
                <h3 className="font-display text-3xl mt-4 text-brand-ink">Inquiry received.</h3>
                <p className="text-brand-inkMuted mt-3">We'll be in touch within 48 hours.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4 bg-brand-bg border border-brand-line p-8" data-testid="speak-form">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="overline text-brand-inkMuted">Name</Label>
                    <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="speak-name" className="rounded-none mt-2" /></div>
                  <div><Label className="overline text-brand-inkMuted">Email</Label>
                    <Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="speak-email" className="rounded-none mt-2" /></div>
                </div>
                <div><Label className="overline text-brand-inkMuted">Organization</Label>
                  <Input value={form.organization} onChange={(e) => set("organization", e.target.value)} data-testid="speak-org" className="rounded-none mt-2" /></div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label className="overline text-brand-inkMuted">Event type</Label>
                    <select value={form.event_type} onChange={(e) => set("event_type", e.target.value)} data-testid="speak-type" className="w-full mt-2 border border-brand-line bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-ink">
                      <option value="keynote">Keynote</option>
                      <option value="workshop">Workshop</option>
                      <option value="panel">Panel</option>
                      <option value="multi-day">Multi-day intensive</option>
                      <option value="other">Other</option>
                    </select></div>
                  <div><Label className="overline text-brand-inkMuted">Date (approx)</Label>
                    <Input type="date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} data-testid="speak-date" className="rounded-none mt-2" /></div>
                  <div><Label className="overline text-brand-inkMuted">Audience size</Label>
                    <Input value={form.audience_size} onChange={(e) => set("audience_size", e.target.value)} placeholder="e.g. 200" data-testid="speak-audience" className="rounded-none mt-2" /></div>
                </div>
                <div><Label className="overline text-brand-inkMuted">Tell us about the event</Label>
                  <Textarea required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="speak-message" className="rounded-none mt-2" /></div>
                <button type="submit" disabled={busy} data-testid="speak-submit" className="w-full bg-brand-ink text-white hover:bg-black py-3 text-sm font-medium tracking-wide transition-colors disabled:opacity-60">
                  {busy ? "Sending…" : "Send inquiry"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
